import type { CSSProperties, ReactNode } from 'react';
import { DT, DS2 } from '../theme/designTokens';
import { useIsMobile } from '../hooks/useBreakpoint';
import { PageContent } from '../components/ui/PageContent';
import { DS2Button } from '../components/ui/DS2Button';
import { Footer } from '../components/shell/Footer';
import { useTranslation } from '../i18n/react';
import { useAuth } from '../context/AuthContext';

/**
 * DELETE ACCOUNT INFO — public, web-reachable account-deletion page (App
 * Store Guideline 5.1.1(v) / Google Play Account Deletion policy: a account
 * deletion path must be reachable from the web WITHOUT installing the app).
 *
 * Deliberately PUBLIC (no RequireAuth), same pattern as RulesScreen: the
 * marketing footer links here for logged-out visitors, and AppShell's chrome
 * already adapts to auth state. Linked from Footer.tsx (Support column).
 *
 * This page does NOT itself delete anything — the frontend ships as a full
 * website (Render static-site deploy), not just an Android wrapper, so "log
 * in on the web and use the same Settings > Danger Zone flow" already
 * satisfies the policy's "web resource, no app install required" bar. There
 * is no second, unauthenticated deletion mechanism here: this app has no
 * password-based identity to verify out-of-band (Firebase-only auth, see
 * ApplicationController#current_user on the backend), so a page that deleted
 * an account from an email address alone would be a takeover vector, not a
 * safety feature. The CTA below sends a logged-out visitor to /login first.
 */
export function DeleteAccountInfoScreen() {
  const { t } = useTranslation(['deleteAccount', 'navigation']);
  const isMobile = useIsMobile();
  const { user, loading } = useAuth();

  return (
    <div style={{ width: '100%', background: DT.color.pageBg, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ paddingTop: isMobile ? 28 : 53, paddingBottom: isMobile ? 28 : 53 }}>
        <PageContent maxWidth={DS2.layout.contentMaxWidth}>
          <h1
            style={{
              fontSize:      isMobile ? 22 : `clamp(1.6rem, ${DS2.type.sectionHeading.pref}vw, ${DS2.type.sectionHeading.max}px)`,
              lineHeight:    isMobile ? '26px' : DS2.type.sectionHeading.lineHeight,
              letterSpacing: '-0.02em',
              fontWeight:    700,
              fontFamily:    DS2.font.heading,
              color:         '#FFFFFF',
              margin:        0,
            }}
          >
            {t('deleteAccount:heading')}
          </h1>
          <p style={{ ...BODY_TEXT, margin: isMobile ? '10px 0 0' : '14.7px 0 0', maxWidth: 760 }}>
            {t('deleteAccount:intro')}
          </p>

          <div style={{ maxWidth: 860 }}>
            <Section title={t('deleteAccount:inApp.title')}>
              <ol style={{ ...BODY_TEXT, margin: '10px 0 0', paddingLeft: 20 }}>
                <li>{t('deleteAccount:inApp.step1')}</li>
                <li style={{ marginTop: 8 }}>{t('deleteAccount:inApp.step2')}</li>
                <li style={{ marginTop: 8 }}>{t('deleteAccount:inApp.step3')}</li>
                <li style={{ marginTop: 8 }}>{t('deleteAccount:inApp.step4')}</li>
              </ol>
              <div style={{ marginTop: 22 }}>
                {!loading && (
                  <DS2Button to={user ? '/settings' : '/login'} variant="gold">
                    {user ? t('deleteAccount:cta.loggedIn') : t('deleteAccount:cta.loggedOut')}
                  </DS2Button>
                )}
              </div>
            </Section>

            <Section title={t('deleteAccount:whatIsDeleted.title')}>
              <p style={BODY_TEXT}>{t('deleteAccount:whatIsDeleted.body')}</p>
            </Section>

            <Section title={t('deleteAccount:whatIsRetained.title')}>
              <p style={BODY_TEXT}>{t('deleteAccount:whatIsRetained.body')}</p>
            </Section>
          </div>
        </PageContent>
      </div>

      <Footer />
    </div>
  );
}

const BODY_TEXT: CSSProperties = {
  fontSize:   'clamp(0.9rem, 0.72vw, 16px)',
  lineHeight: 1.7,
  fontFamily: DS2.font.body,
  fontWeight: 300,
  color:      DS2.color.textBright,
  margin:     '10px 0 0',
};

function Section({ title, children }: { title: string; children: ReactNode }) {
  const isMobile = useIsMobile();
  return (
    <section
      style={{
        background:   DS2.color.surface,
        border:       `1px solid ${DS2.color.cardBorder}`,
        borderRadius: DS2.radius.card,
        padding:      isMobile ? 20 : DS2.spacing.cardPadding,
        marginTop:    isMobile ? 16 : 21,
      }}
    >
      <h2
        style={{
          fontSize:      isMobile ? 18 : `clamp(1.15rem, ${DS2.type.cardTitle.pref}vw, ${DS2.type.cardTitle.max}px)`,
          lineHeight:    1.25,
          letterSpacing: '-0.02em',
          fontWeight:    700,
          fontFamily:    DS2.font.heading,
          color:         '#FFFFFF',
          margin:        0,
        }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}
